import {
  useGetSubscriptionsByAccountQuery,
  useGetSubscriptionsQuery,
} from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { DateTime } from 'luxon';
import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  useGetBuildingByIdQuery,
  useGetBuildingsByAccountQuery,
  useGetBuildingsQuery,
} from '@/api/recalc/endpoints/buildingApiSlice';

import { useGetGatewaysByAccountQuery } from '@/api/recalc/endpoints/gatewayApiSlice';

import { useGetJobSpecificationsQuery } from '@/api/recalc/endpoints/jobSpecificationApiSlice';

import { useGetAccountsQuery } from '@/api/recalc/endpoints/accountsApiSlice';

import {
  useGetActuatorPlanQuery,
  useLazyGetOccupancyPredictionQuery,
  useUpdateMachineLearningModelMutation,
} from '@/api/subscription-manager/predictiveHeatControlSlice';

import {
  useDeleteJobSubscriptionMutation,
  usePostSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from '@/api/recalc/endpoints/jobSubscriptionApiSlice';

import { usePostSubscriptionOnBuildingMutation } from '@/api/recalc/endpoints/buildingApiSlice';

import {
  useGetAlgorithmInputFormSpecificationByJobTypeQuery,
  useGetAlgorithmInputSpecificationByJobTypeQuery,
  useGetOccupancyPredictionAnalysisQuery,
  useLazyGetOccupancyPredictionAnalysisQuery,
} from '../../../api/subscription-manager/algorithmInputSlice';

import GatewayLoadingIndicator from '@/components/indicators/GatewayLoadingIndicator';

import SubscriptionDataGrid from '@/features/building-optimization/components/datagrid/SubscriptionDataGrid';
import AlgorithmInputAdvancedDialog from '@/features/building-optimization/components/dialog/algorithm-input/AlgorithmInputAdvancedDialog';
import AlgorithmInputSimpleDialog from '@/features/building-optimization/components/dialog/algorithm-input/AlgorithmInputSimpleDialog';
import DeleteSubscriptionDialog from '@/features/building-optimization/components/dialog/DeleteSubscriptionDialog';
import JobSubscriptionCreate from '@/features/building-optimization/components/dialog/JobSubscriptionCreate';
import JobSubscriptionEdit from '@/features/building-optimization/components/dialog/JobSubscriptionEdit';
import ResetSubscriptionDialog from '@/features/building-optimization/components/dialog/ResetSubscriptionDialog';
import OccupancyPredictionForm from '@/features/building-optimization/components/OccupancyPredictionForm';

function formatAndEncodeTimestampForAPI(date) {
  // Convert the date to ISO format with the local timezone offset
  const isoString = DateTime.fromJSDate(date).toISO(); // Uses the local time zone by default
  return encodeURIComponent(isoString); // Encode for URL
}

function extractNumberFromURL(url) {
  const matches = url?.match(/(\d+)$/);

  if (matches) {
    return parseInt(matches[1], 10);
  } else {
    return null;
  }
}

const SubscriptionPage = () => {
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // <-- get location here

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [openCreateSubscription, setOpenCreateSubscription] = useState(false);
  const [openEditSubscription, setOpenEditSubscription] = useState(false);
  const [openDeleteSubscription, setOpenDeleteSubscription] = useState(false);
  const [openResetModel, setOpenResetModel] = useState(false);
  const [algorithmInputOpen, setAlgorithmInputOpen] = useState(false);
  const [algorithmInputFormOpen, setAlgorithmInputFormOpen] = useState(false);

  const [futureTimeRange, setFutureTimeRange] = useState({
    start: DateTime.now().toUTC().toMillis(), // Store UTC
    end: DateTime.now().plus({ days: 7 }).toUTC().toMillis(), // Store UTC
  });

  const [modelAnalysisDialogOpen, setModelAnalysisDialogOpen] = useState(false);
  const [generatedOccupancyPlan, setGeneratedOccupancyPlan] = useState(null);
  const [isGeneratingOccupancyAnalysis, setIsGeneratingOccupancyAnalysis] =
    useState(false);

  const { data: accounts } = useGetAccountsQuery();

  const jobType = selectedSubscription?.JobType;

  const { data: algorithmInputSpec } =
    useGetAlgorithmInputSpecificationByJobTypeQuery(
      { jobType },
      { skip: !selectedSubscription?.JobType },
    );

  const { data: algorithmInputFormSpec } =
    useGetAlgorithmInputFormSpecificationByJobTypeQuery(
      { jobType },
      { skip: !selectedSubscription?.JobType },
    );

  const {
    data: allSubscriptions,
    isLoading: isLoadingAllSubscriptions,
    refetch: refetchAllSubscriptions,
  } = useGetSubscriptionsQuery({
    skip: account.AccountId !== 1,
  });

  const {
    data: accountSubscriptions,
    isLoading: isLoadingAccountSubscriptions,
    refetch: refetchAccountSubscriptions,
  } = useGetSubscriptionsByAccountQuery(account?.AccountId, {
    skip: !account.AccountId || account.AccountId === 1,
  });

  const { data: jobSpecifications, isLoading: isLoadingJobSpecifications } =
    useGetJobSpecificationsQuery();

  const { data: building, isLoading: isLoadingBuilding } =
    useGetBuildingByIdQuery(selectedSubscription?.BuildingId, {
      skip: !selectedSubscription?.BuildingId,
    });

  useEffect(() => {
    if (!building) return;
    setSelectedBuilding(building);
  }, [building]);

  const { data: buildings } = useGetBuildingsByAccountQuery(
    account?.AccountId,
    {
      skip: !account?.AccountId || account?.AccountId === 1,
    },
  );

  const { data: allBuildings } = useGetBuildingsQuery({
    skip: account?.AccountId !== 1,
  });

  const { data: gateways, isLoading: isLoadingGateways } =
    useGetGatewaysByAccountQuery(selectedSubscription?.AccountId, {
      skip: !selectedSubscription?.AccountId,
    });

  const { data: publishedActuatorPlan } = useGetActuatorPlanQuery(
    { id: selectedSubscription?.JobSubscriptionId },
    {
      skip:
        !selectedSubscription?.JobSubscriptionId ||
        !selectedSubscription.JobType ||
        selectedSubscription.JobType !== 'PredictiveHeatControl',
    },
  );

  const [fetchOccupancyPredictionAnalysis] =
    useLazyGetOccupancyPredictionAnalysisQuery();

  const [postSubscriptionOnBuilding] = usePostSubscriptionOnBuildingMutation();

  const [getOccupancyPredictions] = useLazyGetOccupancyPredictionQuery();

  const occupancySourceExclusions =
    selectedSubscription?.AlgorithmInput?.predictive_occupancy_detection
      ?.general?.occupancy_source_exclusion || [];

  const { data: occupancyPrediction } = useGetOccupancyPredictionAnalysisQuery(
    {
      jobSubscriptionId: selectedSubscription?.JobSubscriptionId,
      start: formatAndEncodeTimestampForAPI(new Date(futureTimeRange.start)),
      end: formatAndEncodeTimestampForAPI(new Date(futureTimeRange.end)),
      exclusions: occupancySourceExclusions,
    },
    { skip: !selectedSubscription?.JobSubscriptionId },
  );

  const [updateMachineLearningModel] = useUpdateMachineLearningModelMutation();

  const [postSubscription] = usePostSubscriptionMutation();
  const [deleteSubscription] = useDeleteJobSubscriptionMutation();
  const [updateSubscription] = useUpdateSubscriptionMutation();

  const subscriptions =
    account.AccountId === 1
      ? allSubscriptions?.filter((s) => s.JobType === 'PredictiveHeatControl')
      : accountSubscriptions?.filter(
          (s) => s.JobType === 'PredictiveHeatControl',
        );

  const handleViewSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    navigate(
      `/PVO/subscriptions/${encodeURIComponent(subscription.DisplayName)}`,
      {
        state: {
          subscription: subscription,
          building: building,
          from: location, // store current location
        },
      },
    );
  };

  const handleCreateSubscription = async (subscription) => {
    try {
      const response = await postSubscription(subscription).unwrap();
      if (response) {
        dispatch(
          showSnackbar({
            message: 'Subscription was created',
            severity: 'success',
            response: response,
          }),
        );
        const subscriptionId = extractNumberFromURL(response.location);

        if (subscriptionId && selectedBuilding) {
          try {
            const secondResponse = await postSubscriptionOnBuilding({
              buildingId: selectedBuilding?.BuildingId,
              subscriptionId,
            });

            dispatch(
              showSnackbar({
                message: `Subscription was added to ${selectedBuilding?.Name}`,
                severity: 'success',
                response: secondResponse,
              }),
            );
          } catch (error) {
            dispatch(
              showSnackbar({
                message: 'Error adding subscription to building',
                severity: 'error',
                response: error,
              }),
            );
          }
        }
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error creating subscription',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const handleUpdateSubscription = async (subscription) => {
    try {
      const response = await updateSubscription({
        id: selectedSubscription.JobSubscriptionId,
        patch: subscription,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Subscription was updated',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error updating subscription',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const handleResetMachineLearningModel = async (subscription) => {
    setSelectedSubscription(subscription);
    setOpenResetModel(true);
  };

  const handleResetMachineLearningModelConfirmation = async (body) => {
    try {
      await updateMachineLearningModel({
        id: selectedSubscription.JobSubscriptionId,
        data: body,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Machine learning model reset successfully',
          severity: 'success',
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error resetting machine learning model',
          severity: 'error',
          response: error,
        }),
      );
    }
    setOpenResetModel(false);
  };

  const handleDeleteSubscriptionConfirmation = async () => {
    try {
      const response = await deleteSubscription(
        selectedSubscription.JobSubscriptionId,
      ).unwrap();
      dispatch(
        showSnackbar({
          message: 'Subscription was deleted',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error deleting subscription',
          severity: 'error',
          response: error,
        }),
      );
    }
    setOpenDeleteSubscription(false);
  };

  const handleEditSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenEditSubscription(true);
  };

  const handleDeleteSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenDeleteSubscription(true);
  };

  const handleAlgorithmInputOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setAlgorithmInputOpen(true);
  };

  const handleAlgorithmInputFormOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setAlgorithmInputFormOpen(true);
  };

  const handleOccupancyPredictionAnalysisOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setModelAnalysisDialogOpen(true);
  };

  const generateOccupancyPredictionAnalysis = async (formData) => {
    setIsGeneratingOccupancyAnalysis(true); // Start loading

    try {
      const result = await fetchOccupancyPredictionAnalysis({
        jobSubscriptionId: selectedSubscription.JobSubscriptionId,
        duration: formData.duration,
        filterErraticPredictions: formData.filterErraticPredictions,
        exclusions: formData.exclusions,
      });

      // Log the raw result before unwrapping or processing it.

      if (result.data) {
        setGeneratedOccupancyPlan(result.data.occupancy_plan);
      } else {
        dispatch(
          showSnackbar({
            message: result.error.data.detail.ErrorMessage,
            severity: 'error',
          }),
        );
        console.warn('No data in response');
      }
    } catch (error) {
      console.error('Error fetching occupancy prediction analysis:', error);
      setGeneratedOccupancyPlan(null);
    } finally {
      setIsGeneratingOccupancyAnalysis(false); // Stop loading
    }
  };

  return (
    <>
      <Suspense fallback={<div>Loading TreeComponent...</div>}>
        <GatewayLoadingIndicator
          isLoading={
            isLoadingAllSubscriptions ||
            isLoadingAccountSubscriptions ||
            isLoadingBuilding ||
            isLoadingJobSpecifications ||
            isLoadingGateways
          }
        />
      </Suspense>
      <Suspense fallback={<div>Loading Building Info...</div>}>
        <SubscriptionDataGrid
          subscriptions={
            subscriptions?.filter(
              (s) => s.JobType === 'PredictiveHeatControl',
            ) || []
          }
          accounts={accounts}
          onSubscriptionCreate={() => setOpenCreateSubscription(true)}
          onSubscriptionDelete={handleDeleteSubscriptionConfirmation}
          handleAlgorithmInputFormOpen={handleAlgorithmInputFormOpen}
          handleAlgorithmInputOpen={handleAlgorithmInputOpen}
          handleOccupancyPredictionAnalysisOpen={
            handleOccupancyPredictionAnalysisOpen
          }
          handleViewSubscription={handleViewSubscription}
          handleEditSubscription={handleEditSubscription}
          handleDeleteSubscription={handleDeleteSubscription}
          handleResetMachineLearningModel={handleResetMachineLearningModel}
          refetchSubscriptions={
            account.AccountId === 1
              ? refetchAllSubscriptions
              : refetchAccountSubscriptions
          }
        />
      </Suspense>

      {!isLoadingBuilding &&
        !isLoadingGateways &&
        !isLoadingJobSpecifications && (
          <Suspense fallback={<div>Loading Create Subscription Dialog...</div>}>
            <JobSubscriptionCreate
              open={openCreateSubscription}
              onClose={() => setOpenCreateSubscription(false)}
              buildings={buildings || allBuildings}
              selectedBuilding={selectedBuilding}
              setSelectedBuilding={setSelectedBuilding}
              jobSpecifications={jobSpecifications}
              submit={handleCreateSubscription}
            />
          </Suspense>
        )}

      {!isLoadingBuilding &&
        !isLoadingGateways &&
        !isLoadingJobSpecifications && (
          <Suspense fallback={<div>Loading Create Subscription Dialog...</div>}>
            <JobSubscriptionEdit
              open={openEditSubscription}
              onClose={() => setOpenEditSubscription(false)}
              submit={handleUpdateSubscription}
              subscription={selectedSubscription}
              location={building?.Location}
              jobSpecifications={jobSpecifications}
              gateways={gateways}
            />
          </Suspense>
        )}

      <Suspense fallback={<div>Loading Delete Subscription Dialog...</div>}>
        <DeleteSubscriptionDialog
          open={openDeleteSubscription}
          onClose={() => setOpenDeleteSubscription(false)}
          subscription={selectedSubscription}
          handleDeleteSubscription={handleDeleteSubscriptionConfirmation}
        />
      </Suspense>

      <ResetSubscriptionDialog
        open={openResetModel}
        onClose={() => setOpenResetModel(false)}
        subscription={selectedSubscription}
        handleResetSubscription={handleResetMachineLearningModelConfirmation}
      />

      <Suspense fallback={<div>Loading algorithm input dialog...</div>}>
        <AlgorithmInputAdvancedDialog
          open={algorithmInputOpen}
          onClose={() => setAlgorithmInputOpen(false)}
          subscription={selectedSubscription}
          update={handleUpdateSubscription}
          specificationData={algorithmInputSpec}
        />
      </Suspense>

      {/* <Suspense fallback={<div>Loading algorithm input dialog...</div>}> */}
      <AlgorithmInputSimpleDialog
        open={algorithmInputFormOpen}
        onClose={() => setAlgorithmInputFormOpen(false)}
        subscription={selectedSubscription}
        algorithmInput={selectedSubscription?.AlgorithmInput}
        update={handleUpdateSubscription}
        specificationData={algorithmInputFormSpec}
        updateMachineLearningModel={updateMachineLearningModel}
      />
      {/* </Suspense> */}

      <Suspense fallback={<div>Loading occupancy prediction form...</div>}>
        <OccupancyPredictionForm
          open={modelAnalysisDialogOpen}
          onClose={() => {
            setModelAnalysisDialogOpen(false);
            setGeneratedOccupancyPlan(null);
          }}
          subscription={selectedSubscription}
          actuatorPlan={publishedActuatorPlan}
          initialPrediction={occupancyPrediction}
          occupancySourceExclusions={occupancySourceExclusions}
          generateOccupancyPredictionAnalysis={
            generateOccupancyPredictionAnalysis
          }
          setGeneratedOccupancyPlan={() => setGeneratedOccupancyPlan(null)}
          generatedOccupancyPlan={generatedOccupancyPlan}
          isGenerating={isGeneratingOccupancyAnalysis}
        />
      </Suspense>
    </>
  );
};

export default SubscriptionPage;
